import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Route, Router, ActivatedRoute, NavigationEnd, Event as RouterEvent, RouterModule } from "@angular/router";

import { EmitterService } from "../../services/emitter.service";
import { IButton } from "../../interfaces/ibuttons";
import { IAppStore } from "../../iappstore";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { IUser } from "../../interfaces/ilogin";
import { IBreadCrumb } from "../../interfaces/ibreadcrumb";

/**
 * Header component. This is a reusable component used to put on header on every admin page.
 * title : This is an input parameter which goes as Title on the page.
 * headingClass: This is an input parameter which goes as ngclass for the heading.
 * This allows you to customise the an image on top of each header page.
 * buttons: This is an input parameter which allows the user to add multiple buttons on the page. It takes an array of IButton
 * buttonsClicked: This is an output parameter, gets populated with button click events from the buttons array passed in.
 * User do not have to pass in the events
 */
@Component({
    selector: "header-bar",
    standalone: true,
    imports: [CommonModule, RouterModule],
    templateUrl: "./header.component.html",
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class HeaderComponent implements OnInit, OnDestroy {
    @Input() pageTitle: string;
    @Input() headingClass: string;
    @Input() buttons: IButton[] = new Array<IButton>();
    @Output() buttonsClicked: EventEmitter<any>[] = new Array<EventEmitter<any>>();

    tabs: Route[];
    breadcrumbs: IBreadCrumb[];
    currentRouteTitle: string;
    navEnd$: Subscription;

    constructor(private activatedRoute: ActivatedRoute, private router: Router, private emitter: EmitterService, private store: Store<IAppStore>) {
        activatedRoute.url.subscribe(() => {

            let children: Route[] = activatedRoute.snapshot.routeConfig.children;
            let user: IUser = null;

            store.select(e => e.user)
                .subscribe(e => user = e);
            store.select(e => e.user.selectedBusinessFunction)
                .subscribe((selectedBusinessFunction) => {
                    if (user || (selectedBusinessFunction || selectedBusinessFunction.id)) {
                        this.router.config.forEach(item => {
                            // build up menu items based on navigation data
                            if (item.data && item.data['navigation'] && item.data['security']) {
                                const roles: string[] = item.data['security'].roles as string[];
                                const rolesFound: string[] = roles.filter((r: string) => user.roles.indexOf(r) > -1);
                                if (rolesFound.length > 0) {
                                    if (children) {
                                        this.tabs = children.filter((child: Route) => {
                                            return child.path !== "" && (rolesFound.filter((i) => {
                                                return child.data['security'].roles.indexOf(i) > -1;
                                            }).length > 0);
                                        });
                                    }
                                }
                            }
                        });

                    }
                });
        });

        this.navEnd$ = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: RouterEvent) => {
            this.buildBreadCrumbs();
        });
    }

    ngOnInit(): void {
        this.buttons.forEach(b => this.buttonsClicked.push(EmitterService.get(b.label)));
    }

    ngOnDestroy(): void {
        this.navEnd$.unsubscribe();
    }

    onButtonClick(buttonIndex: number): void {
        this.buttonsClicked[buttonIndex].emit();
    }

    buildBreadCrumbs(): void {

        this.breadcrumbs = [];
        this.currentRouteTitle = "";
        const currentRouteData: any = this.activatedRoute.snapshot.data;
        const breadcrumbData: any = currentRouteData["breadcrumbs"];
        if (!breadcrumbData) {
            return;
        }
        this.currentRouteTitle = currentRouteData["title"];
        breadcrumbData.forEach((i: any) => {
            const route: Route = this.router.config.find((r: Route) => {
                return r.path === i.path;
            });

            // handle exceptional breadcrumb routing scenarios
            if (route.path === "dropdown") {
                this.breadcrumbs.push({ title: `Drop down: ${currentRouteData.dropDown.description}`, url: route.path + "/" + currentRouteData.dropDown.id });
            } else if (route.path === "metadata") {
                this.breadcrumbs.push({ title: `Manage metadata`, url: "categories/managemetadata"});
            } else {
                this.breadcrumbs.push({ title: route.data["title"], url: route.path });
            }
        });

    }
}
